import React, { useEffect, useState, useCallback, useRef } from 'react';
import AdminLayout from './AdminLayout';
import { motion, AnimatePresence } from 'framer-motion';
import apiManager from '../../../lib/api';
import { StoreCategory, StoreLocation } from '../../../lib/api/services/store';
import { useJsApiLoader, GoogleMap, Marker } from '@react-google-maps/api';
import { Map, MapPin, Plus, Trash2, Edit3, UploadCloud, AlertTriangle, X, ChevronDown, ChevronUp } from 'lucide-react';

const mapContainerStyle = { width: '100%', height: '100%' };
const center = { lat: 39.4891, lng: -4.9845 };
const libraries: ("places")[] = ["places"];

const StoreCategoriesPage = () => {
  const [categories, setCategories] = useState<StoreCategory[]>([]);
  const [stores, setStores] = useState<StoreLocation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [editingCat, setEditingCat] = useState<Partial<StoreCategory> | null>(null);
  
  const [expandedCatId, setExpandedCatId] = useState<string | null>(null);
  const [isStoreModalOpen, setIsStoreModalOpen] = useState(false);
  const [editingStore, setEditingStore] = useState<Partial<StoreLocation> | null>(null);
  
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadingFile, setUploadingFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Usamos la const libraries para evitar warnings del api loadaer
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '',
    libraries: libraries
  });

  const mapRef = useRef<google.maps.Map | null>(null);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const catRes = await apiManager.stores.getCategories();
      if (catRes.success && catRes.data) setCategories(catRes.data);
      else setError('Error al cargar las categorías.');

      const storeRes = await apiManager.stores.getStores({ limit: 5000 });
      if(storeRes.success && storeRes.data) setStores(storeRes.data);
    } catch (err) {
      console.error(err);
      setError('Error de red.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  const handleStatusChange = async (categoryId: string, newStatus: boolean) => {
    try {
      const response = await apiManager.stores.updateCategory(categoryId, { isActive: newStatus });
      if (response.success) {
        setCategories(prev => prev.map(cat => cat._id === categoryId ? { ...cat, isActive: newStatus } : cat));
        setStores(prevStores => prevStores.map(store => {
          if (store.category && store.category._id === categoryId) {
            return { ...store, category: { ...store.category, isActive: newStatus } };
          }
          return store;
        }));
      }
    } catch (err) {
      alert('Error al actualizar visibilidad.');
    }
  };

  const handleDelete = async (categoryId: string) => {
    if (!window.confirm('¿Seguro que deseas eliminar permanentemente esta categoría y todas las tiendas dentro de ella?')) return;
    try {
      await apiManager.stores.deleteCategory(categoryId);
      setCategories(prev => prev.filter(c => c._id !== categoryId));
      setStores(prevStores => prevStores.filter(store => store.category?._id !== categoryId));
    } catch (err) {
      alert('Error al eliminar categoría.');
    }
  };

  const handleSaveCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if(!editingCat?.name) return;
    
    try {
      if (editingCat._id) {
        // Update
        const res = await apiManager.stores.updateCategory(editingCat._id, { name: editingCat.name, pinColor: editingCat.pinColor });
        if(res.success && res.data) {
           setCategories(prev => prev.map(c => c._id === res.data!._id ? res.data! : c));
           setStores(prevStores => prevStores.map(store => store.category?._id === res.data!._id ? { ...store, category: { ...store.category, ...res.data } } : store));
        }
      } else {
        // Create manual
        const res = await apiManager.stores.createCategory({ name: editingCat.name, pinColor: editingCat.pinColor || '#3B82F6', isActive: true });
        if(res.success && res.data) {
           setCategories([...categories, res.data]);
        }
      }
      setIsCategoryModalOpen(false);
    } catch (err) {
      alert('Error al guardar.');
    }
  };

  const handleDeleteStore = async (storeId: string) => {
    if (!window.confirm('¿Seguro que deseas eliminar esta tienda individual?')) return;
    try {
      await apiManager.stores.deleteStore(storeId);
      setStores(prev => prev.filter(s => s._id !== storeId));
    } catch(err) { alert('Error al eliminar tienda.'); }
  };

  const handleSaveStore = async (e: React.FormEvent) => {
    e.preventDefault();
    if(!editingStore?.name || !editingStore.location?.coordinates || !editingStore.category) return;
    
    setIsUploading(true);
    const payload = {
      name: editingStore.name,
      address: editingStore.address || '',
      category: typeof editingStore.category === 'object' ? editingStore.category._id : editingStore.category,
      location: { coordinates: editingStore.location.coordinates },
      contactInfo: editingStore.contactInfo,
      isActive: editingStore.isActive !== undefined ? editingStore.isActive : true
    } as any;

    try {
      if (editingStore._id) {
         const res = await apiManager.stores.updateStore(editingStore._id, payload);
         if(res.success && res.data) setStores(prev => prev.map(s => s._id === res.data!._id ? res.data! : s));
      } else {
         const res = await apiManager.stores.createStore(payload);
         if(res.success && res.data) setStores([...stores, res.data]);
      }
      setIsStoreModalOpen(false);
      setEditingStore(null);
    } catch(err) { alert('Error guardando la tienda.'); } finally { setIsUploading(false); }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setUploadingFile(e.target.files[0]);
      setIsUploadModalOpen(true);
    }
    // Reset para permitir subir el mismo archivo en caso de fallo
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const confirmUpload = async () => {
    if(!uploadingFile) return;
    setIsUploading(true);
    try {
      const res = await apiManager.stores.uploadKMZ(uploadingFile);
      if(res.success) {
         setIsUploadModalOpen(false);
         setUploadingFile(null);
         alert(res.message || 'Mapa importado exitosamente.');
         await fetchData(); // Recargar mapa entero
      } else {
         alert('Error subiendo: ' + JSON.stringify(res));
      }
    } catch(err) {
      alert('Ocurrió un error general subiendo el fichero al servidor.');
      console.error(err);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <AdminLayout title="Mapa">
      
      {/* Nuevo Banner Descriptivo Moderno */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded-r-lg shadow-sm flex flex-col md:flex-row md:justify-between items-start md:items-center">
        <div className="flex">
          <div className="flex-shrink-0">
            <Map className="h-6 w-6 text-blue-500" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-bold text-blue-900">
              Gestor Principal Cartográfico
            </h3>
            <p className="text-sm text-blue-800 mt-1 flex flex-col md:flex-row md:space-x-1">
              <span>Administra la visibilidad global de las marcas y puntos de venta.</span>
              <span className="font-semibold underline truncate max-w-sm hidden lg:inline">Los marcadores ocultos desaparecen de internet al instante.</span>
            </p>
          </div>
        </div>

        {/* Acciones principales */}
        <div className="mt-4 md:mt-0 flex space-x-2 w-full md:w-auto ml-10 md:ml-0">
           <button 
             onClick={() => { setEditingCat({name: '', pinColor: '#2563EB'}); setIsCategoryModalOpen(true); }}
             className="flex items-center text-sm font-medium bg-white text-gray-700 px-3 py-2 border border-gray-300 rounded shadow-sm hover:bg-gray-50 flex-1 md:flex-initial"
           >
             <Plus size={16} className="mr-1.5" /> Nuevo Grupo
           </button>
           <button 
             onClick={handleUploadClick}
             className="flex items-center text-sm font-medium bg-blue-600 text-white px-3 py-2 border border-blue-600 rounded shadow-sm hover:bg-blue-700 flex-1 md:flex-initial justify-center"
           >
             <UploadCloud size={16} className="mr-1.5" /> Importar KMZ/KML
           </button>
           <input type="file" ref={fileInputRef} className="hidden" accept=".kml,.kmz,.xml,.zip" onChange={handleFileChange} />
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
        </div>
      ) : error ? (
        <div className="p-4 text-red-700 bg-red-100 rounded-md shadow-sm border border-red-200">
          <p>{error}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-auto lg:h-[calc(100vh-280px)] min-h-[600px]">
          
          {/* Col 1: Panel de Categorías */}
          <div className="bg-white shadow rounded-xl border border-gray-100 flex flex-col overflow-hidden">
            <div className="p-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
              <h2 className="text-base font-bold text-gray-800">Grupos de Distribuidores</h2>
              <span className="bg-white border border-gray-200 text-xs font-semibold px-2 py-1 rounded shadow-sm text-gray-600">Total: {categories.length}</span>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-white sticky top-0 shadow-sm z-10">
                  <tr>
                    <th className="px-5 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider w-2/4">Categoría</th>
                    <th className="px-5 py-3 text-center text-[11px] font-bold text-gray-500 uppercase tracking-wider">Estado</th>
                    <th className="px-5 py-3 text-right text-[11px] font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">Gestionar</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-50">
                  {categories.map((category, index) => (
                    <React.Fragment key={category._id}>
                    <motion.tr
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className={`transition-colors ${category.isActive ? 'hover:bg-gray-50' : 'bg-gray-50/50'}`}
                    >
                      <td className="px-5 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-3">
                          <label className="relative inline-flex items-center cursor-pointer" title="Activar/Ocultar a clientes">
                            <input type="checkbox" className="sr-only peer" checked={category.isActive} onChange={() => handleStatusChange(category._id, !category.isActive)} />
                            <div className="w-8 h-4 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-green-500"></div>
                          </label>
                          <div className="w-3.5 h-3.5 rounded-full shadow-inner ring-1 ring-black/10" style={{ backgroundColor: category.pinColor }} />
                          <div className={`text-sm font-semibold truncate max-w-[120px] md:max-w-xs ${category.isActive ? 'text-gray-900' : 'text-gray-400'}`}>{category.name}</div>
                        </div>
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap text-center">
                        <span className={`px-2 py-1 inline-flex text-[10px] leading-4 font-bold uppercase tracking-wider rounded-md ${category.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {category.isActive ? 'Visible' : 'Oculto'}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-right whitespace-nowrap">
                        <div className="flex justify-end space-x-2">
                           <button onClick={() => { setEditingCat(category); setIsCategoryModalOpen(true); }} className="text-gray-400 hover:text-blue-600 transition-colors bg-gray-50 p-1.5 rounded-md hover:bg-blue-50 border border-transparent hover:border-blue-200" title="Editar Categoría">
                             <Edit3 size={16} />
                           </button>
                           <button onClick={() => handleDelete(category._id)} className="text-gray-400 hover:text-red-600 transition-colors bg-gray-50 p-1.5 rounded-md hover:bg-red-50 border border-transparent hover:border-red-200" title="Eliminar este grupo y sus pines">
                             <Trash2 size={16} />
                           </button>
                           <button onClick={() => setExpandedCatId(expandedCatId === category._id ? null : category._id)} className={`transition-colors p-1.5 rounded-md border border-transparent ${expandedCatId === category._id ? 'bg-blue-100 text-blue-700' : 'bg-gray-50 text-gray-400 hover:bg-blue-50 hover:text-blue-600'}`} title="Ver Distribuidores">
                             {expandedCatId === category._id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                           </button>
                        </div>
                      </td>
                    </motion.tr>
                    {expandedCatId === category._id && (
                      <tr className="bg-blue-50/30">
                        <td colSpan={3} className="px-5 py-4 border-b border-gray-100 p-0 m-0">
                          <div className="bg-white rounded-lg border border-blue-100 shadow-sm overflow-hidden mb-2 mt-2 ml-4 mr-4">
                            <div className="bg-blue-50/50 px-4 py-2 border-b border-blue-100 flex justify-between items-center">
                               <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide">Puntos de Venta ({stores.filter(s => s.category?._id === category._id).length})</h4>
                               <button 
                                 onClick={() => { setEditingStore({ name: '', address: '', location: { coordinates: [0, 0] }, category: category, isActive: true } as any); setIsStoreModalOpen(true); }}
                                 className="text-[10px] font-bold bg-white text-blue-600 px-2.5 py-1 rounded shadow-sm border border-blue-200 hover:bg-blue-50 flex items-center"
                               >
                                 <Plus size={12} className="mr-1" /> Tienda
                               </button>
                            </div>
                            <ul className="divide-y divide-gray-50 max-h-64 overflow-y-auto">
                               {stores.filter(s => s.category?._id === category._id).map((store) => (
                                 <li key={store._id} className="py-2.5 px-4 hover:bg-gray-50 flex justify-between items-start group">
                                    <div className="pr-4">
                                      <p className="text-xs font-bold text-gray-900 group-hover:text-blue-600 transition-colors">{store.name}</p>
                                      {store.address && <p className="text-[10px] text-gray-500 mt-0.5 line-clamp-1">{store.address}</p>}
                                    </div>
                                    <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                       <button onClick={() => { setEditingStore(store); setIsStoreModalOpen(true); }} className="p-1 text-gray-400 hover:text-blue-600"><Edit3 size={14}/></button>
                                       <button onClick={() => handleDeleteStore(store._id)} className="p-1 text-gray-400 hover:text-red-600"><Trash2 size={14}/></button>
                                    </div>
                                 </li>
                               ))}
                               {stores.filter(s => s.category?._id === category._id).length === 0 && (
                                 <li className="py-6 text-center text-xs text-gray-400">Ningún punto de venta registrado.</li>
                               )}
                            </ul>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  ))}
                  {categories.length === 0 && (
                    <tr><td colSpan={3} className="p-8 text-center text-gray-400">No hay categorías. Crea una manualmente o Importa los datos.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Col 2: Previsualización del Mapa */}
          <div className="bg-white shadow rounded-xl border border-gray-100 flex flex-col overflow-hidden relative min-h-[400px]">
            <div className="absolute top-4 left-4 z-10 bg-white/95 backdrop-blur-md p-3 sm:p-4 rounded-xl shadow-lg border border-gray-100 pointer-events-none">
              <h4 className="text-xs font-bold text-gray-900 flex items-center mb-1.5"><MapPin size={16} className="mr-1.5 text-blue-500" strokeWidth={2.5} /> Preview </h4>
              <p className="text-[10px] sm:text-xs leading-relaxed text-gray-600 max-w-[280px] hidden sm:block">
                Los pines activos se pre-renderizan a continuación inmediatamente.
              </p>
              <div className="mt-2 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">{stores.filter(s => s.category?.isActive).length} Pines visibles</div>
            </div>

            <div className="flex-1 w-full bg-gray-50 relative pointer-events-auto">
              {!isLoaded ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50">
                  <Map className="w-8 h-8 text-gray-300 animate-pulse mb-3" />
                </div>
              ) : (
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  zoom={5.8}
                  center={center}
                  onLoad={onMapLoad}
                  options={{
                    disableDefaultUI: false,
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: false,
                    styles: [ { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] } ]
                  }}
                >
                  {stores.filter(s => s.category?.isActive).map((store) => (
                      <Marker
                        key={`admin-marker-${store._id}`}
                        position={{ lat: store.location.coordinates[1], lng: store.location.coordinates[0] }}
                        icon={{
                          path: google.maps.SymbolPath.CIRCLE,
                          fillColor: store.category?.pinColor || '#3B82F6',
                          fillOpacity: 1,
                          strokeColor: '#FFFFFF',
                          strokeWeight: 1.5,
                          scale: 6,
                        }}
                        title={`${store.name}`}
                      />
                    ))}
                </GoogleMap>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODALES OVERLAY */}
      <AnimatePresence>
        {isStoreModalOpen && (
           <div className="fixed inset-0 z-50 overflow-y-auto">
             <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
               <div className="fixed inset-0 transition-opacity" onClick={() => setIsStoreModalOpen(false)}>
                 <div className="absolute inset-0 bg-gray-900 opacity-60 backdrop-blur-sm"></div>
               </div>
               <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
               <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="inline-block relative z-10 align-bottom bg-white rounded-xl text-left shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-xl w-full">
                 <form onSubmit={handleSaveStore}>
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 rounded-t-xl">
                      <h3 className="text-lg leading-6 font-bold text-gray-900 mb-4">{editingStore?._id ? 'Editar Tienda' : 'Nuevo Distribuidor de la Marca'}</h3>
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Nombre</label>
                          <input type="text" required value={editingStore?.name || ''} onChange={e => setEditingStore({...editingStore, name: e.target.value})} className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-blue-500" placeholder="Ej. Peluquería Canina Sur" />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-700 uppercase mb-1" title="Si cambias esto, el marker visualmente cambiará a otro color">Grupo Comercial (Categoría)</label>
                          <select required value={typeof editingStore?.category === 'object' ? editingStore?.category._id : editingStore?.category || ''} onChange={e => setEditingStore({...editingStore, category: categories.find(c => c._id === e.target.value) as any})} className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-gray-50 focus:ring-blue-500 outline-none">
                            {categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Dirección (Descriptiva)</label>
                          <input type="text" value={editingStore?.address || ''} onChange={e => setEditingStore({...editingStore, address: e.target.value})} className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-blue-500" placeholder="Ej. Calle Alcalá 42" />
                        </div>
                        <div className="grid grid-cols-2 gap-4 bg-gray-50 border border-gray-100 p-3 rounded-lg">
                           <div>
                             <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Latitud (Y)</label>
                             <input type="number" step="any" required value={editingStore?.location?.coordinates?.[1] || 0} onChange={e => setEditingStore({...editingStore, location: { coordinates: [editingStore?.location?.coordinates?.[0] || 0, parseFloat(e.target.value)] }})} className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs text-right font-mono" />
                           </div>
                           <div>
                             <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Longitud (X)</label>
                             <input type="number" step="any" required value={editingStore?.location?.coordinates?.[0] || 0} onChange={e => setEditingStore({...editingStore, location: { coordinates: [parseFloat(e.target.value), editingStore?.location?.coordinates?.[1] || 0] }})} className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs text-right font-mono" />
                           </div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-100 rounded-b-xl">
                      <button type="submit" disabled={isUploading} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm transition-colors">
                        {isUploading ? 'Guardando...' : 'Guardar Pin'}
                      </button>
                      <button type="button" onClick={() => setIsStoreModalOpen(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">
                        Cerrar
                      </button>
                    </div>
                 </form>
               </motion.div>
             </div>
           </div>
        )}

      {/* MODAL VIEW CATEGORY */}
        {isCategoryModalOpen && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={() => setIsCategoryModalOpen(false)}>
                <div className="absolute inset-0 bg-gray-900 opacity-60 backdrop-blur-sm"></div>
              </div>
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
              <motion.div 
                 initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                 className="inline-block relative z-10 align-bottom bg-white rounded-xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full"
              >
                <form onSubmit={handleSaveCategory}>
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                      <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                        <Edit3 className="h-5 w-5 text-blue-600" aria-hidden="true" />
                      </div>
                      <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                        <h3 className="text-lg leading-6 font-bold text-gray-900">{editingCat?._id ? 'Editar Categoría' : 'Nueva Categoría de Marca'}</h3>
                        <div className="mt-4 space-y-4">
                           <div>
                             <label className="block text-sm font-medium text-gray-700">Nombre de la Categoría</label>
                             <input type="text" required value={editingCat?.name || ''} onChange={e => setEditingCat({...editingCat, name: e.target.value})} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Ej. Distribuidores VIP" />
                           </div>
                           <div>
                             <label className="block text-sm font-medium text-gray-700">Personalizar Color del Marcador (Pin)</label>
                             <div className="mt-1 flex items-center space-x-3">
                                <input type="color" value={editingCat?.pinColor || '#3B82F6'} onChange={e => setEditingCat({...editingCat, pinColor: e.target.value})} className="h-9 w-14 p-0.5 border border-gray-300 rounded shadow-sm cursor-pointer" />
                                <span className="text-sm font-mono text-gray-500 bg-gray-50 px-2 py-1 rounded border border-gray-100">{editingCat?.pinColor || '#3B82F6'}</span>
                             </div>
                           </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-100">
                    <button type="submit" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm">
                      Guardar Cambios
                    </button>
                    <button type="button" onClick={() => setIsCategoryModalOpen(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">
                      Cancelar
                    </button>
                  </div>
                </form>
              </motion.div>
            </div>
          </div>
        )}

        {isUploadModalOpen && (
          <div className="fixed inset-0 z-[60] overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" onClick={() => !isUploading && setIsUploadModalOpen(false)}>
                <div className="absolute inset-0 bg-gray-900 opacity-80 backdrop-blur-sm"></div>
              </div>
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
              <motion.div 
                 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
                 className="inline-block relative z-10 align-bottom bg-white rounded-xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md w-full border border-gray-200"
              >
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="absolute top-4 right-4"><button disabled={isUploading} onClick={() => setIsUploadModalOpen(false)} className="text-gray-400 hover:text-gray-600"><X size={20}/></button></div>
                  <div className="sm:flex sm:items-start flex-col">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-14 w-14 rounded-full bg-red-100 sm:mx-auto mb-4 border border-red-200">
                      <AlertTriangle className="h-7 w-7 text-red-600" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:text-center w-full">
                      <h3 className="text-xl leading-6 font-extrabold text-gray-900 mb-2">Advertencia de Sobreescritura</h3>
                      <p className="text-sm text-gray-600 mb-4 px-2">
                        Estás a punto de importar la información desde el fichero: <br/> 
                        <span className="font-mono bg-gray-100 px-2 py-0.5 rounded text-blue-600 text-xs mt-1 inline-block border border-gray-200 truncate max-w-[200px]">{uploadingFile?.name}</span>
                      </p>
                      <div className="bg-red-50 p-3 rounded-lg border border-red-100 text-left relative overflow-hidden">
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-red-500"></div>
                        <p className="text-sm text-red-800 font-medium pl-2">Al continuar, el sistema borrará irreversiblemente: <br/>• Todas las categorías actuales de la Base de datos.<br/>• Todos los cientos o miles de Puntos de Venta anclados al mapa.</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-4 sm:px-6 flex flex-col sm:flex-row-reverse border-t border-gray-100">
                  <button type="button" disabled={isUploading} onClick={confirmUpload} className="w-full inline-flex justify-center items-center rounded-lg border border-transparent shadow-md px-4 py-2.5 bg-red-600 text-base font-bold text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto sm:text-sm transition-colors">
                    {isUploading ? <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white/40 border-t-white mr-2"/> Importando Mapa...</> : <><UploadCloud size={16} className="mr-2"/> Borrar y Subir Mapa</>}
                  </button>
                  <button type="button" disabled={isUploading} onClick={() => setIsUploadModalOpen(false)} className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2.5 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:w-auto sm:text-sm">
                    Cancelar
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </AdminLayout>
  );
};

export default StoreCategoriesPage;
